'use client';

import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '@/config/theme';
import { i18n } from '@/config/i18n';
import { Tooltip } from '@/components/ui-components';
import { CloseIconRed } from '@/components/icons';

const Chip = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: ${theme.colors.white};
  color: ${theme.colors.gray[900]};
  font-size: 12px;
  line-height: 1.2;
  border-radius: 4px;
  border: 1px solid ${theme.colors.border.primary};
  white-space: nowrap;
  max-width: 200px;
  text-overflow: ellipsis;
  position: relative;
  height: 24px;
  transition: background-color 0.2s;

  &:has(button:hover) {
    background: #FEF3F2;
  }
`;

const ChipText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 160px;
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background: transparent;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition: background-color 0.2s;
  color: ${theme.colors.gray[500]};

  &:hover {
    background: ${theme.colors.gray[100]};
    color: ${theme.colors.status.error};
  }

  &:focus {
    outline: 2px solid ${theme.colors.primary.accent};
    outline-offset: 1px;
  }

  svg {
    width: 12px;
    height: 12px;
    stroke: currentColor;
    stroke-width: 2;
  }
`;

export interface EmailChipProps {
  email: string;
  onRemove: (e: React.MouseEvent) => void;
  disabled?: boolean;
}

export const EmailChip: React.FC<EmailChipProps> = ({ email, onRemove, disabled = false }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const check = () => setIsTruncated(el.clientWidth > 154);
    check();
  }, [email]);
  
  const chip = (
    <Chip>
      <ChipText ref={textRef}>{email}</ChipText>
      <Tooltip title={i18n.common.buttons.remove} position="bottom">
        <RemoveButton
          type="button"
          onClick={onRemove}
          aria-label={`${i18n.common.buttons.remove} ${email}`}
          disabled={disabled}
        >
          <CloseIconRed width={10} height={10} />
        </RemoveButton>
      </Tooltip>
    </Chip>
  );

  return isTruncated ? (
    <Tooltip title={email} position="top">
      {chip}
    </Tooltip>
  ) : (
    chip
  );
};

export default EmailChip;
